import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChartLineUp, CurrencyCircleDollar, TrendUp, TrendDown,
  Plus, Trash, Receipt, CalendarBlank, Target,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fc = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function DREPage() {
  const [dre, setDre] = useState(null);
  const { t } = useLanguage();
  const [cmv, setCmv] = useState(null);
  const [sales, setSales] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [saleForm, setSaleForm] = useState({ technical_sheet_id: "", quantity: "1", revenue: "", date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [dreRes, cmvRes, salesRes, sheetsRes] = await Promise.all([
        axios.get(`${API}/dre`),
        axios.get(`${API}/cmv`),
        axios.get(`${API}/sales`),
        axios.get(`${API}/technical-sheets`),
      ]);
      setDre(dreRes.data);
      setCmv(cmvRes.data);
      setSales(salesRes.data);
      setSheets(sheetsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados financeiros");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSale = async () => {
    if (!saleForm.technical_sheet_id || !saleForm.revenue) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/sales`, {
        technical_sheet_id: saleForm.technical_sheet_id,
        quantity: parseInt(saleForm.quantity) || 1,
        revenue: parseFloat(saleForm.revenue) || 0,
        date: saleForm.date,
      });
      toast.success("Venda registrada!");
      setSaleDialogOpen(false);
      setSaleForm({ technical_sheet_id: "", quantity: "1", revenue: "", date: new Date().toISOString().slice(0, 10) });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao registrar venda");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm("Deletar esta venda?")) return;
    try {
      await axios.delete(`${API}/sales/${saleId}`);
      toast.success("Venda removida");
      fetchData();
    } catch (error) {
      toast.error("Erro ao deletar");
    }
  };

  // Auto-calc revenue suggestion when sheet is selected
  const selectedSheet = sheets.find((s) => s.id === saleForm.technical_sheet_id);

  const getCmvColor = (pct) => {
    if (pct === 0) return "text-zinc-400";
    if (pct <= 30) return "text-emerald-700";
    if (pct <= 40) return "text-amber-700";
    return "text-rose-700";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dre-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("dre_title")}</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("dre_subtitle")}</p>
        </div>
        <Button onClick={() => setSaleDialogOpen(true)} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="add-sale-btn">
          <Plus size={16} className="mr-2" /> {t("dre_register_sale")}
        </Button>
      </div>

      <Tabs defaultValue="dre" className="w-full">
        <TabsList className="grid w-full grid-cols-3 text-xs md:text-sm">
          <TabsTrigger value="dre" data-testid="tab-dre">DRE Mensal</TabsTrigger>
          <TabsTrigger value="cmv" data-testid="tab-cmv">CMV Histórico</TabsTrigger>
          <TabsTrigger value="vendas" data-testid="tab-vendas">Vendas</TabsTrigger>
        </TabsList>

        {/* DRE Tab */}
        <TabsContent value="dre" className="mt-4 space-y-4">
          {dre && (
            <>
              {/* DRE Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-zinc-200 shadow-sm">
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Receita Bruta</p>
                    <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-1" data-testid="dre-revenue">{fc(dre.revenue)}</p>
                  </CardContent>
                </Card>
                <Card className="border-zinc-200 shadow-sm">
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className="text-[10px] uppercase tracking-wider text-rose-600 font-bold">CMV</p>
                    <p className={`text-lg md:text-2xl font-mono font-bold mt-1 ${getCmvColor(dre.cmv_percentage)}`} data-testid="dre-cmv">{fc(dre.cmv)}</p>
                    <p className={`text-xs font-semibold mt-0.5 ${getCmvColor(dre.cmv_percentage)}`}>{dre.cmv_percentage}%</p>
                  </CardContent>
                </Card>
                <Card className="border-zinc-200 shadow-sm">
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Custos Operacionais</p>
                    <p className="text-lg md:text-2xl font-mono font-bold text-zinc-700 mt-1">{fc(dre.operational_costs)}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{dre.production_days} dias × R$ 15</p>
                  </CardContent>
                </Card>
                <Card className={`border-2 shadow-sm ${dre.net_profit >= 0 ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}>
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className={`text-[10px] uppercase tracking-wider font-bold ${dre.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>Lucro Líquido</p>
                    <p className={`text-lg md:text-2xl font-mono font-bold mt-1 ${dre.net_profit >= 0 ? "text-emerald-800" : "text-rose-800"}`} data-testid="dre-net-profit">{fc(dre.net_profit)}</p>
                    <p className={`text-xs font-semibold mt-0.5 ${dre.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      Margem: {dre.net_margin}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* DRE Table */}
              <Card className="border-zinc-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-bold flex items-center gap-2">
                    <Receipt size={18} /> DRE Simplificado — {dre.month}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-zinc-100">
                      <span className="text-sm text-zinc-700">Receita Bruta ({dre.sales_count} vendas)</span>
                      <span className="font-mono font-bold text-zinc-900">{fc(dre.revenue)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-zinc-100">
                      <span className="text-sm text-rose-700">(-) CMV ({dre.cmv_percentage}%)</span>
                      <span className="font-mono font-semibold text-rose-700">- {fc(dre.cmv)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-zinc-200 font-semibold">
                      <span className="text-sm text-zinc-900">= Lucro Bruto</span>
                      <span className="font-mono text-zinc-900">{fc(dre.gross_profit)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-zinc-100">
                      <span className="text-sm text-zinc-600">(-) Custos Operacionais</span>
                      <span className="font-mono text-zinc-600">- {fc(dre.operational_costs)}</span>
                    </div>
                    <div className={`flex justify-between py-3 rounded-lg px-3 mt-1 ${dre.net_profit >= 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
                      <span className={`text-sm font-bold ${dre.net_profit >= 0 ? "text-emerald-800" : "text-rose-800"}`}>= LUCRO LÍQUIDO</span>
                      <span className={`font-mono font-bold text-lg ${dre.net_profit >= 0 ? "text-emerald-800" : "text-rose-800"}`}>{fc(dre.net_profit)}</span>
                    </div>
                  </div>

                  {/* CMV Target */}
                  <div className="mt-4 p-3 bg-zinc-50 border border-zinc-200 rounded-lg flex items-center gap-3">
                    <Target size={20} className="text-zinc-500 shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-zinc-700">Meta CMV: 28% a 35%</p>
                      <p className="text-xs text-zinc-500">
                        Seu CMV atual é <span className={`font-bold ${getCmvColor(dre.cmv_percentage)}`}>{dre.cmv_percentage}%</span>.
                        {dre.cmv_percentage > 0 && dre.cmv_percentage <= 35 && " Dentro da meta!"}
                        {dre.cmv_percentage > 35 && " Acima da meta — revise custos ou preços."}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* CMV History Tab */}
        <TabsContent value="cmv" className="mt-4">
          {cmv && cmv.monthly_history.length > 0 ? (
            <Card className="border-zinc-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-bold flex items-center gap-2">
                  <ChartLineUp size={18} /> Histórico Mensal de CMV
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-zinc-200 rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50">
                        <TableHead className="text-xs font-semibold">Mês</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Receita</TableHead>
                        <TableHead className="text-xs font-semibold text-right">CMV</TableHead>
                        <TableHead className="text-xs font-semibold text-center">CMV%</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Lucro</TableHead>
                        <TableHead className="text-xs font-semibold text-center">Vendas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cmv.monthly_history.map((m) => (
                        <TableRow key={m.month}>
                          <TableCell className="text-sm font-medium">{m.month}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{fc(m.revenue)}</TableCell>
                          <TableCell className="text-sm text-right font-mono text-rose-700">{fc(m.cost)}</TableCell>
                          <TableCell className="text-center">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${getCmvColor(m.cmv_percentage)} ${m.cmv_percentage <= 35 ? "bg-emerald-100" : m.cmv_percentage <= 40 ? "bg-amber-100" : "bg-rose-100"}`}>
                              {m.cmv_percentage}%
                            </span>
                          </TableCell>
                          <TableCell className={`text-sm text-right font-mono font-bold ${m.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fc(m.profit)}</TableCell>
                          <TableCell className="text-sm text-center">{m.sales_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <ChartLineUp size={48} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-500">Nenhuma venda registrada ainda.</p>
                <p className="text-sm text-zinc-400 mt-1">Registre vendas para ver o histórico de CMV.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="vendas" className="mt-4">
          {sales.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CurrencyCircleDollar size={48} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-500">Nenhuma venda registrada.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-zinc-200 shadow-sm">
              <CardContent className="pt-4">
                <div className="border border-zinc-200 rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50">
                        <TableHead className="text-xs font-semibold">Data</TableHead>
                        <TableHead className="text-xs font-semibold">Produto</TableHead>
                        <TableHead className="text-xs font-semibold text-center">Qtd</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Custo</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Receita</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Lucro</TableHead>
                        <TableHead className="text-xs font-semibold text-center">Ação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sales.slice(0, 50).map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell className="text-xs text-zinc-500">{new Date(sale.date + "T12:00:00").toLocaleDateString("pt-BR")}</TableCell>
                          <TableCell className="text-sm font-medium">{sale.technical_sheet_name}</TableCell>
                          <TableCell className="text-sm text-center font-mono">x{sale.quantity}</TableCell>
                          <TableCell className="text-sm text-right font-mono text-rose-600">{fc(sale.cost)}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{fc(sale.revenue)}</TableCell>
                          <TableCell className={`text-sm text-right font-mono font-bold ${sale.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fc(sale.profit)}</TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-700"
                              onClick={() => handleDeleteSale(sale.id)}>
                              <Trash size={14} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Register Sale Dialog */}
      <Dialog open={saleDialogOpen} onOpenChange={setSaleDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <CurrencyCircleDollar size={20} /> Registrar Venda
            </DialogTitle>
            <DialogDescription className="sr-only">Registrar uma nova venda</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Ficha Técnica</Label>
              <Select value={saleForm.technical_sheet_id} onValueChange={(v) => setSaleForm({ ...saleForm, technical_sheet_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="sale-sheet-select">
                  <SelectValue placeholder="Selecione o produto vendido..." />
                </SelectTrigger>
                <SelectContent>
                  {sheets.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} (Custo: {fc(s.total_cost)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSheet && (
                <p className="text-xs text-zinc-500 mt-1">
                  Custo unitário: {fc(selectedSheet.total_cost)} · {selectedSheet.yield_quantity} {selectedSheet.yield_unit}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Quantidade</Label>
                <Input type="number" min="1" value={saleForm.quantity}
                  onChange={(e) => setSaleForm({ ...saleForm, quantity: e.target.value })}
                  className="mt-1.5" data-testid="sale-quantity" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Data</Label>
                <Input type="date" value={saleForm.date}
                  onChange={(e) => setSaleForm({ ...saleForm, date: e.target.value })}
                  className="mt-1.5" data-testid="sale-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                Valor Recebido (R$) <span className="text-red-500">*</span>
              </Label>
              <Input type="number" step="0.01" value={saleForm.revenue}
                onChange={(e) => setSaleForm({ ...saleForm, revenue: e.target.value })}
                placeholder="Ex: 150.00" className="mt-1.5 text-lg font-mono" data-testid="sale-revenue" />
            </div>
            {selectedSheet && saleForm.revenue && (
              <div className={`p-3 rounded-lg border ${parseFloat(saleForm.revenue) > selectedSheet.total_cost * (parseInt(saleForm.quantity) || 1) ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                <div className="flex justify-between text-xs">
                  <span>Custo: {fc(selectedSheet.total_cost * (parseInt(saleForm.quantity) || 1))}</span>
                  <span className="font-bold">
                    Lucro: {fc(parseFloat(saleForm.revenue) - selectedSheet.total_cost * (parseInt(saleForm.quantity) || 1))}
                  </span>
                </div>
              </div>
            )}
            <Button onClick={handleAddSale} disabled={saving}
              className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="save-sale-btn">
              {saving ? "Salvando..." : "Registrar Venda"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
